// React hook for offline queue integration
// Wraps offlineQueue with React state and auto-sync on reconnect

import { useState, useEffect, useCallback, useRef } from "react";
import {
  initDB,
  EventTypes,
  queueEvent as dbQueueEvent,
  getQueuedEvents,
  getPendingCount,
  getPendingCountsByType,
  markSynced,
  markFailed,
  getBlob,
} from "../lib/offlineQueue";
import { useOnlineStatus } from "./useOnlineStatus";
import { syncEvent } from "../lib/syncService";

/**
 * Hook for managing offline queue with React state
 * @returns {object} Queue state and methods
 */
export function useOfflineQueue() {
  const { isOnline, isChecking } = useOnlineStatus();

  const [pendingCount, setPendingCount] = useState(0);
  const [pendingByType, setPendingByType] = useState({});
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Track if sync is in progress to prevent concurrent syncs
  const syncInProgress = useRef(false);
  // Track if we should auto-sync when coming online
  const pendingSync = useRef(false);

  /**
   * Refresh pending counts from IndexedDB
   */
  const refreshCounts = useCallback(async () => {
    try {
      const count = await getPendingCount();
      const byType = await getPendingCountsByType();
      setPendingCount(count);
      setPendingByType(byType);
    } catch (error) {
      console.error("Failed to refresh queue counts:", error);
    }
  }, []);

  /**
   * Queue an event for later sync
   */
  const queueEvent = useCallback(
    async (type, payload, metadata, blob = null) => {
      try {
        const event = await dbQueueEvent(type, payload, metadata, blob);
        await refreshCounts();

        // If online, trigger immediate sync
        if (isOnline && !syncInProgress.current) {
          pendingSync.current = true;
        }

        return event;
      } catch (error) {
        console.error("Failed to queue event:", error);
        throw error;
      }
    },
    [isOnline, refreshCounts],
  );

  /**
   * Process a single event - upload and mark synced
   */
  const processEvent = useCallback(async (event) => {
    try {
      // Get blob if this event has one
      let blob = null;
      if (event.blobKey) {
        blob = await getBlob(event.blobKey);
      }

      // Sync the event (upload to server)
      await syncEvent(event, blob);

      // Mark as synced and remove from queue
      await markSynced(event.id);

      return { success: true, eventId: event.id };
    } catch (error) {
      console.error(`Failed to sync event ${event.id}:`, error);

      // Mark as failed and increment retry count
      const updatedEvent = await markFailed(event.id);

      return {
        success: false,
        eventId: event.id,
        error: error.message,
        retryCount: updatedEvent.retryCount,
      };
    }
  }, []);

  /**
   * Sync all pending events
   */
  const syncAll = useCallback(async () => {
    if (syncInProgress.current || !isOnline) {
      return { synced: 0, failed: 0 };
    }

    syncInProgress.current = true;
    setIsSyncing(true);
    setSyncError(null);

    let synced = 0;
    let failed = 0;

    try {
      // Get all pending events
      const events = await getQueuedEvents("pending");

      // Also retry failed events with < 3 retries
      const failedEvents = await getQueuedEvents("failed");
      const retryableEvents = failedEvents.filter(
        (e) => (e.retryCount || 0) < 3,
      );

      const allEvents = [...events, ...retryableEvents];

      // Process events in order
      for (const event of allEvents) {
        // Check if still online
        if (!navigator.onLine) {
          break;
        }

        const result = await processEvent(event);
        if (result.success) {
          synced++;
        } else {
          failed++;
        }

        // Update counts after each event
        await refreshCounts();
      }

      setLastSyncTime(new Date().toISOString());

      return { synced, failed };
    } catch (error) {
      console.error("Sync failed:", error);
      setSyncError(error.message);
      return { synced, failed, error: error.message };
    } finally {
      syncInProgress.current = false;
      setIsSyncing(false);
      pendingSync.current = false;
    }
  }, [isOnline, processEvent, refreshCounts]);

  // Initialize DB and load counts on mount
  useEffect(() => {
    initDB().then(() => {
      refreshCounts();
    });
  }, [refreshCounts]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && pendingCount > 0 && !syncInProgress.current) {
      syncAll();
    }
  }, [isOnline, pendingCount, syncAll]);

  // Trigger sync when pendingSync flag is set
  useEffect(() => {
    if (pendingSync.current && isOnline && !syncInProgress.current) {
      syncAll();
    }
  }, [isOnline, syncAll]);

  return {
    // State
    isOnline,
    isChecking,
    isSyncing,
    syncError,
    lastSyncTime,
    pendingCount,
    pendingByType,

    // Methods
    queueEvent,
    syncAll,
    refreshCounts,

    // Event types for convenience
    EventTypes,
  };
}

export default useOfflineQueue;
